import React, { useEffect, useState } from 'react';
import {
  Copy,
  Check,
  MessageSquare,
  Mail,
  Share2,
  ChevronDown,
  ExternalLink,
} from 'lucide-react';

/**
 * MenuShareCard — restaurant menu sharing UX (post-onboarding & dashboard).
 *
 * Mobile-first, US-market priorities (in CTA order):
 *  1. Native "Share" button (Web Share API) → Messages, AirDrop, Gmail, etc.
 *  2. Text Message (SMS / iMessage) — `sms:` URL scheme
 *  3. Email — `mailto:` with pre-filled subject + body
 *  4. WhatsApp — `wa.me`
 *  5. Copy Link
 *
 * `light` prop renders the white/light variant for screens with light bg
 * (onboarding share page). Default = dark variant (dashboard).
 */

const isIOS = () => {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

const MenuShareCard = ({
  restaurantName = 'our restaurant',
  menuUrl,
  light = false,
  className = '',
}) => {
  const [copied, setCopied] = useState(false);
  const [hasNativeShare, setHasNativeShare] = useState(false);

  useEffect(() => {
    setHasNativeShare(typeof navigator !== 'undefined' && typeof navigator.share === 'function');
  }, []);

  const shortMessage =
    `Order directly from ${restaurantName} with 0% commission fees:\n${menuUrl}`;
  const emailSubject = `Check out ${restaurantName}'s online ordering menu`;
  const emailBody =
    `Order directly from our restaurant with 0% commission fees — better prices for you, more support for us!\n\n${menuUrl}\n\nPowered by NoFee.`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(menuUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* noop */
    }
  };

  const handleNativeShare = async () => {
    if (!navigator.share) {
      copyLink();
      return;
    }
    try {
      await navigator.share({
        title: `${restaurantName} — Order online`,
        text: shortMessage,
        url: menuUrl,
      });
    } catch {
      /* user cancelled */
    }
  };

  const openSMS = () => {
    const body = encodeURIComponent(shortMessage);
    const separator = isIOS() ? '&' : '?';
    window.location.href = `sms:${separator}body=${body}`;
  };

  const openEmail = () => {
    window.location.href = `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
  };

  const openWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shortMessage)}`, '_blank', 'noopener');
  };

  // Style palettes for light vs dark surfaces
  const linkRow = light
    ? 'bg-white border-neutral-200 text-neutral-900'
    : 'bg-black/50 border-gold-500/20 text-white/80';
  const linkText = light ? 'text-neutral-700' : 'text-white/80';
  const copyTxt = light ? 'text-gold-600 hover:text-gold-700' : 'text-gold-400 hover:text-gold-300';
  const secondaryBtn = light
    ? 'bg-white hover:bg-neutral-50 active:bg-neutral-100 border-neutral-200 hover:border-gold-500/40 text-neutral-900'
    : 'bg-white/[0.06] hover:bg-white/[0.10] active:bg-white/[0.14] border-white/15 hover:border-gold-400/40 text-white';

  return (
    <div className={`space-y-3 ${className}`} data-testid="menu-share-card">
      {/* Link row */}
      <div className={`flex items-center gap-2 p-3 rounded-xl border ${linkRow}`}>
        <ExternalLink className={`w-4 h-4 ${light ? 'text-gold-600' : 'text-gold-400'} shrink-0`} />
        <input
          readOnly
          value={menuUrl}
          className={`flex-1 bg-transparent text-xs sm:text-sm outline-none ${linkText} min-w-0`}
          data-testid="menu-share-url"
          onClick={(e) => e.target.select()}
        />
        <button
          onClick={copyLink}
          data-testid="menu-share-copy-inline"
          className={`inline-flex items-center gap-1 text-xs font-bold shrink-0 px-2 ${copyTxt}`}
        >
          {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>

      {/* Action grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {hasNativeShare && (
          <button
            onClick={handleNativeShare}
            data-testid="menu-share-native"
            className="col-span-1 sm:col-span-2 inline-flex items-center justify-center gap-2 w-full px-5 py-4 rounded-2xl bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 active:from-amber-400 active:to-gold-600 text-black font-extrabold text-[15px] transition-all shadow-[0_15px_40px_-15px_rgba(212,175,55,0.6)]"
          >
            <Share2 className="w-4 h-4" />
            Share Menu
          </button>
        )}

        <button
          onClick={openSMS}
          data-testid="menu-share-sms"
          className={`inline-flex items-center justify-center gap-2 w-full px-4 py-3.5 rounded-2xl border font-bold text-[14px] transition-all ${secondaryBtn}`}
        >
          <MessageSquare className="w-4 h-4 text-emerald-500" />
          Text Message
        </button>

        <button
          onClick={openEmail}
          data-testid="menu-share-email"
          className={`inline-flex items-center justify-center gap-2 w-full px-4 py-3.5 rounded-2xl border font-bold text-[14px] transition-all ${secondaryBtn}`}
        >
          <Mail className="w-4 h-4 text-sky-500" />
          Email Menu
        </button>

        <button
          onClick={openWhatsApp}
          data-testid="menu-share-whatsapp"
          className={`inline-flex items-center justify-center gap-2 w-full px-4 py-3.5 rounded-2xl border font-bold text-[14px] transition-all ${secondaryBtn}`}
        >
          <svg className="w-4 h-4 text-green-500" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
          </svg>
          WhatsApp
        </button>

        <button
          onClick={copyLink}
          data-testid="menu-share-copy"
          className={`inline-flex items-center justify-center gap-2 w-full px-4 py-3.5 rounded-2xl border font-bold text-[14px] transition-all ${secondaryBtn}`}
        >
          {copied ? (
            <Check className="w-4 h-4 text-emerald-500" />
          ) : (
            <Copy className="w-4 h-4 text-gold-500" />
          )}
          {copied ? 'Copied' : 'Copy Link'}
        </button>
      </div>
    </div>
  );
};

export default MenuShareCard;
